import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Heart, 
  History, 
  FolderHeart, 
  Trash2, 
  Plus, 
  MoreVertical,
  ChevronRight,
  LogOut,
  User as UserIcon,
  Search,
  LayoutGrid,
  Zap,
  TrendingUp,
  Star,
  Award,
  Crown,
  ShoppingBag
} from 'lucide-react';
import { Layout } from '@/components/Layout';
import { TutorialCard } from '@/components/TutorialCard';
import { useAppData } from '@/hooks/useAppData';
import { useUserActions } from '@/hooks/useUserActions';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/db/supabase';
import { cn } from '@/lib/utils';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger,
  DialogFooter
} from "@/components/ui/dialog";
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from "@/components/ui/dropdown-menu";

export default function Profile() {
  const { tutorials, categories } = useAppData();
  const { user, profile, signOut } = useAuth();
  const { 
    favorites, 
    history, 
    customFolders, 
    toggleFavorite, 
    addHistory, 
    clearHistory, 
    createFolder, 
    deleteFolder,
    toggleTutorialInFolder 
  } = useUserActions();

  const [isAddingFolder, setIsAddingFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState('all');
  const [userBadges, setUserBadges] = useState<any[]>([]);

  // 等级计算逻辑
  const userLevel = profile?.level || 1;
  const userPoints = profile?.points || 0;
  const nextLevelPoints = userLevel * userLevel * 200; // 简单的等级曲线
  const progress = Math.min((userPoints / nextLevelPoints) * 100, 100);

  const getLevelInfo = (level: number) => {
    const levels: Record<number, { name: string; icon: any; color: string }> = {
      1: { name: '萌新创作者', icon: <Star className="h-3 w-3" />, color: 'bg-muted text-muted-foreground' },
      2: { name: '潜力新星', icon: <Zap className="h-3 w-3" />, color: 'bg-blue-500/10 text-blue-500' },
      3: { name: '资深博主', icon: <TrendingUp className="h-3 w-3" />, color: 'bg-primary/10 text-primary' },
      4: { name: '内容大咖', icon: <Award className="h-3 w-3" />, color: 'bg-secondary/10 text-secondary' },
      5: { name: '万象宗师', icon: <Crown className="h-3 w-3" />, color: 'bg-amber-500/10 text-amber-500' },
    };
    return levels[level] || levels[1];
  };

  const levelInfo = getLevelInfo(userLevel);

  // 获取用户勋章
  useEffect(() => {
    const fetchUserBadges = async () => {
      if (!user) return;
      const { data } = await supabase
        .from('user_badges')
        .select('*, badge_shop(*)')
        .eq('user_id', user.id)
        .limit(6);
      setUserBadges(data || []);
    };
    fetchUserBadges();
  }, [user]);

  // Favorites
  const favoritedTutorials = tutorials.filter((t) => favorites.includes(t.id));
  const filteredFavorites = selectedCategoryId === 'all' 
    ? favoritedTutorials 
    : favoritedTutorials.filter(t => t.categoryId === selectedCategoryId);

  // History
  const historyTutorials = history.map((h) => {
    const t = tutorials.find((tut) => tut.id === h.tutorialId);
    return t ? { ...t, timestamp: h.timestamp } : null;
  }).filter((t) => t !== null) as any[];

  const handleCreateFolder = () => {
    if (newFolderName.trim()) {
      const success = createFolder(newFolderName.trim());
      if (success) {
        setNewFolderName('');
        setIsAddingFolder(false);
      } else {
        alert('收藏夹数量已达上限（20个）');
      }
    }
  };

  return (
    <Layout>
      <div className="container mx-auto px-4 md:px-6 py-12">
        {/* User Info Header */}
        <div className="flex flex-col md:flex-row items-center gap-8 mb-12 bg-card p-8 rounded-3xl border border-border/50 shadow-sm relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-full -translate-y-16 translate-x-16" />
          
          <div className="relative">
            <div className="h-24 w-24 rounded-full bg-primary/10 border-4 border-background flex items-center justify-center overflow-hidden">
              <UserIcon className="h-12 w-12 text-primary" />
            </div>
            <div className="absolute bottom-0 right-0 h-6 w-6 bg-green-500 rounded-full border-2 border-background" />
          </div>

          <div className="flex-1 text-center md:text-left">
            <div className="flex items-center justify-center md:justify-start gap-3 mb-1">
              <h1 className="text-3xl font-extrabold">{profile?.username || '创作者'}</h1>
              <Badge className={cn("px-2 py-0.5 rounded-full flex items-center gap-1", levelInfo.color)}>
                {levelInfo.icon} LV.{userLevel}
              </Badge>
            </div>
            
            <div className="flex flex-col gap-2 mb-4">
              <div className="flex items-center justify-center md:justify-start gap-2 text-muted-foreground text-sm font-medium">
                <Star className="h-4 w-4 text-primary" />
                <span>{levelInfo.name}</span>
                <span className="mx-1 opacity-30">|</span>
                <span>自媒体积分: {userPoints}</span>
              </div>
              
              <div className="max-w-[240px] mx-auto md:mx-0">
                <div className="flex justify-between text-[10px] text-muted-foreground mb-1">
                  <span>下一等级: LV.{userLevel + 1}</span>
                  <span>{Math.floor(progress)}%</span>
                </div>
                <Progress value={progress} className="h-1.5 bg-primary/10" />
              </div>
            </div>

            <div className="flex flex-wrap justify-center md:justify-start gap-4">
              <Button 
                size="sm" 
                variant="outline" 
                className="rounded-xl border-border/50"
                onClick={() => {}}
              >
                修改资料
              </Button>
              <Button 
                size="sm" 
                variant="default" 
                className="rounded-xl bg-primary text-white font-bold"
                asChild
              >
                <Link to="/badge-shop">
                  <ShoppingBag className="h-4 w-4 mr-2" /> 勋章商城
                </Link>
              </Button>
              <Button 
                size="sm" 
                variant="ghost" 
                className="text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/20 rounded-xl"
                onClick={signOut}
              >
                <LogOut className="h-4 w-4 mr-2" /> 退出登录
              </Button>
            </div>
          </div>
          
          <div className="grid grid-cols-3 gap-6 md:gap-12 px-6">
            <div className="text-center">
              <p className="text-2xl font-bold">{favorites.length}</p>
              <p className="text-xs text-muted-foreground uppercase tracking-widest">收藏</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold">{history.length}</p>
              <p className="text-xs text-muted-foreground uppercase tracking-widest">历史</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold">{customFolders.length}</p>
              <p className="text-xs text-muted-foreground uppercase tracking-widest">文件夹</p>
            </div>
          </div>
        </div>

        <Tabs defaultValue="favorites" className="w-full">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <TabsList className="bg-muted/50 p-1">
              <TabsTrigger value="favorites" className="flex items-center gap-2">
                <Heart className="h-4 w-4" /> 我的收藏
              </TabsTrigger>
              <TabsTrigger value="folders" className="flex items-center gap-2">
                <FolderHeart className="h-4 w-4" /> 收藏夹
              </TabsTrigger>
              <TabsTrigger value="history" className="flex items-center gap-2">
                <History className="h-4 w-4" /> 浏览历史
              </TabsTrigger>
              <TabsTrigger value="badges" className="flex items-center gap-2">
                <Award className="h-4 w-4" /> 我的勋章
              </TabsTrigger>
            </TabsList>

            {/* Sub-filtering for favorites */}
            <TabsContent value="favorites" className="mt-0">
              <div className="flex gap-2 overflow-x-auto no-scrollbar py-1">
                <Button 
                  size="sm" 
                  variant={selectedCategoryId === 'all' ? 'default' : 'outline'}
                  onClick={() => setSelectedCategoryId('all')}
                >
                  全部
                </Button>
                {categories.map(cat => (
                  <Button
                    key={cat.id}
                    size="sm"
                    variant={selectedCategoryId === cat.id ? 'default' : 'outline'}
                    onClick={() => setSelectedCategoryId(cat.id)}
                    className="whitespace-nowrap"
                  >
                    {cat.name}
                  </Button>
                ))}
              </div>
            </TabsContent>
          </div>

          <TabsContent value="favorites">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {filteredFavorites.map((tutorial) => (
                <div key={tutorial.id} className="relative group">
                  <TutorialCard
                    tutorial={tutorial}
                    isFavorited={true}
                    onFavoriteToggle={toggleFavorite}
                    onClick={addHistory}
                  />
                  <div className="absolute top-2 right-12 opacity-0 group-hover:opacity-100 transition-opacity">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="secondary" size="icon" className="h-8 w-8 rounded-full shadow-md">
                          <Plus className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {customFolders.map(folder => (
                          <DropdownMenuItem key={folder.id} onClick={() => toggleTutorialInFolder(folder.id, tutorial.id)}>
                            <div className="flex items-center justify-between w-full">
                              <span>加入 {folder.name}</span>
                              {folder.tutorialIds.includes(tutorial.id) && <Badge className="h-2 w-2 rounded-full p-0" />}
                            </div>
                          </DropdownMenuItem>
                        ))}
                        {customFolders.length === 0 && <DropdownMenuItem disabled>暂无收藏夹</DropdownMenuItem>}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              ))}
            </div>
            {filteredFavorites.length === 0 && (
              <div className="py-24 text-center border rounded-2xl bg-muted/20">
                <Heart className="h-12 w-12 text-muted-foreground mx-auto mb-4 opacity-20" />
                <h3 className="text-lg font-bold mb-2">暂无收藏内容</h3>
                <p className="text-muted-foreground">快去浏览教程并收藏吧！</p>
              </div>
            )}
          </TabsContent>

          <TabsContent value="folders">
            <div className="flex justify-end mb-6">
              <Dialog open={isAddingFolder} onOpenChange={setIsAddingFolder}>
                <DialogTrigger asChild>
                  <Button className="flex items-center gap-2">
                    <Plus className="h-4 w-4" /> 新建收藏夹
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>新建收藏夹</DialogTitle>
                  </DialogHeader>
                  <div className="py-4">
                    <Input 
                      placeholder="收藏夹名称 (最多 20 字)" 
                      value={newFolderName}
                      onChange={(e) => setNewFolderName(e.target.value.slice(0, 20))}
                    />
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setIsAddingFolder(false)}>取消</Button>
                    <Button onClick={handleCreateFolder}>确认创建</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {customFolders.map((folder) => (
                <div key={folder.id} className="bg-card border rounded-2xl p-6 hover:shadow-md transition-all group">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="bg-primary/10 p-2 rounded-lg">
                        <FolderHeart className="h-5 w-5 text-primary" />
                      </div>
                      <h3 className="text-lg font-bold">{folder.name}</h3>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem className="text-red-500" onClick={() => deleteFolder(folder.id)}>
                          <Trash2 className="h-4 w-4 mr-2" /> 删除收藏夹
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  <p className="text-sm text-muted-foreground mb-6">
                    内含 {folder.tutorialIds.length} 个项目
                  </p>
                  <div className="grid grid-cols-4 gap-2 mb-6 h-12">
                    {folder.tutorialIds.slice(0, 4).map((tid, idx) => (
                      <div key={tid} className="bg-muted rounded-md border flex items-center justify-center text-[10px] font-bold text-muted-foreground overflow-hidden">
                        {idx === 3 && folder.tutorialIds.length > 4 ? `+${folder.tutorialIds.length - 3}` : <LayoutGrid className="h-4 w-4 opacity-30" />}
                      </div>
                    ))}
                    {folder.tutorialIds.length === 0 && (
                      <div className="col-span-4 bg-muted/30 rounded-md border border-dashed flex items-center justify-center text-xs text-muted-foreground italic">
                        空收藏夹
                      </div>
                    )}
                  </div>
                  <Button variant="outline" className="w-full justify-between" asChild>
                    <Link to={`#folder-${folder.id}`}>
                      查看详情 <ChevronRight className="h-4 w-4" />
                    </Link>
                  </Button>
                </div>
              ))}
            </div>

            {customFolders.length === 0 && (
              <div className="py-24 text-center border rounded-2xl bg-muted/20">
                <FolderHeart className="h-12 w-12 text-muted-foreground mx-auto mb-4 opacity-20" />
                <h3 className="text-lg font-bold mb-2">暂无自定义收藏夹</h3>
                <p className="text-muted-foreground">创建收藏夹以更好地分类管理资源</p>
              </div>
            )}
          </TabsContent>

          <TabsContent value="history">
            <div className="flex justify-end mb-6">
              <Button variant="ghost" className="text-red-500" onClick={clearHistory}>
                <Trash2 className="h-4 w-4 mr-2" /> 清空历史记录
              </Button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {historyTutorials.map((tutorial) => (
                <div key={`${tutorial.id}-${tutorial.timestamp}`} className="relative">
                  <TutorialCard
                    tutorial={tutorial}
                    isFavorited={favorites.includes(tutorial.id)}
                    onFavoriteToggle={toggleFavorite}
                    onClick={addHistory}
                  />
                  <div className="absolute top-2 left-2 pointer-events-none">
                    <Badge variant="secondary" className="bg-background/80 backdrop-blur text-[10px]">
                      {new Date(tutorial.timestamp).toLocaleDateString()}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
            {historyTutorials.length === 0 && (
              <div className="py-24 text-center border rounded-2xl bg-muted/20">
                <History className="h-12 w-12 text-muted-foreground mx-auto mb-4 opacity-20" />
                <h3 className="text-lg font-bold mb-2">暂无浏览记录</h3>
                <p className="text-muted-foreground">快去看看那些优质的教程吧！</p>
              </div>
            )}
          </TabsContent>

          <TabsContent value="badges">
            <div className="space-y-6">
              {/* 勋章墙 */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                {userBadges.map((item: any) => (
                  <div key={item.id} className="bg-card border border-border/50 rounded-2xl p-4 flex flex-col items-center justify-center hover:shadow-lg transition-all duration-300 group">
                    <div className="text-4xl mb-2 group-hover:scale-110 transition-transform">{item.badge_shop.icon}</div>
                    <p className="text-xs font-bold text-center line-clamp-1">{item.badge_shop.name}</p>
                    <Badge variant="outline" className="mt-2 text-[10px]">
                      {item.badge_shop.type === 'badge' ? '勋章' : item.badge_shop.type === 'avatar_frame' ? '头像框' : '称号'}
                    </Badge>
                  </div>
                ))}
              </div>

              {userBadges.length === 0 && (
                <div className="py-24 text-center border rounded-2xl bg-muted/20">
                  <Award className="h-12 w-12 text-muted-foreground mx-auto mb-4 opacity-20" />
                  <h3 className="text-lg font-bold mb-2">暂无勋章</h3>
                  <p className="text-muted-foreground mb-6">前往勋章商城兑换专属勋章吧！</p>
                  <Button asChild>
                    <Link to="/badge-shop">
                      <ShoppingBag className="h-4 w-4 mr-2" /> 前往勋章商城
                    </Link>
                  </Button>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
